// built-from: /chat-component v3 impl:chat-component role:implementation
// The one reusable chat control. Hosts supply discovery or facilitation data;
// this hook alone composes transport, voice, imports, transcript and turn state.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  streamChat2,
  applyActivityEvent,
  type ChatActivityEvent,
  type DoneModel,
} from '../utils/chat2Stream';
import { stripDsnFromText } from '../utils/summaryParser';
import type { NewModel } from '../utils/flowModel';
import type {
  AttachmentRecord,
  ChatActivity,
  ChatController,
  ConversationMessage,
  ConversationMode,
  DiscoveryFeaturesPayload,
  DocImportResult,
  SurfaceAction,
} from './sessionTypes';
import type { ScreenChatContext } from './screenChatContext';
import { turnFactsFrom } from '../utils/turnFacts';
import { useEngagement } from './useEngagement';
import { useImports } from './useImports';
import { useVoice } from './useVoice';
import { createFacilitationId } from '../facilitation/commandIdentity';

export type { ChatController } from './sessionTypes';

export interface FacilitationChatCopy {
  openingFallback: (hour: number) => string;
  sendFailure: string;
  resumeFallback: string;
  resumeFailure: string;
}

export interface CandidateItem {
  slotId: string;
  member?: string;
  value: string;
  source: string;
  rationale?: string;
}

export interface AcceptedCandidate {
  slotId: string;
  member?: string;
  value: string;
}

export interface DiscoveryChatConfiguration {
  kind: 'discovery';
  sessionId: string | null;
  mode: ConversationMode;
  // 083: an alternate server chat2 context for discovery-style sessions
  // (e.g. 'event-storming' — same streaming pipeline, different grammar
  // prompt). Omitted = the default discovery context.
  contextId?: string;
  memorySummary: string;
  newModel: NewModel;
  setNewModel: Dispatch<SetStateAction<NewModel>>;
  setDiscoveryFeatures: (payload: DiscoveryFeaturesPayload) => void;
  applyTurnResult: (model: DoneModel) => void;
  transitionOffered: boolean;
  dismissOffer: () => void;
  sessionError?: string | null;
}

export interface FacilitationChatConfiguration {
  kind: 'facilitation';
  sessionId: string | null;
  screenContext: ScreenChatContext;
  onTurnFactEvents?: (events: unknown[]) => void;
  onSurfaceActions?: (actions: SurfaceAction[]) => void;
  copy?: FacilitationChatCopy;
  // 077: a listening surface (the Brief) passes false so spoken turns stay
  // silent — tools still run and the unspoken reply lands in the transcript.
  // Omitted keeps today's speaking posture for every other facilitated screen.
  speakReplies?: boolean;
}

export type UseChatConfiguration =
  | DiscoveryChatConfiguration
  | FacilitationChatConfiguration;

export interface UseChatReturn extends ChatController {
  openSession: () => Promise<void>;
  resumeSession: () => Promise<void>;
  candidates: CandidateItem[];
  acceptCandidates: (accepted: AcceptedCandidate[]) => Promise<void>;
  historySeeded: boolean;
  isImportingDocument: boolean;
  importFromMermaid: (
    args: { mermaid?: string; flowText?: string; force?: boolean },
  ) => Promise<DocImportResult>;
  isImportingMermaid: boolean;
  setFlowText: (
    args: { flowText: string; force?: boolean },
  ) => Promise<DocImportResult>;
  isSavingFlowText: boolean;
  replaceConversationHistory: (history: ConversationMessage[]) => void;
  resetConversationHistory: () => void;
}

const DEFAULT_COPY: FacilitationChatCopy = Object.freeze({
  openingFallback: () => 'We could not start the guided conversation just now. Please try again.',
  sendFailure: 'Your message was not sent. The working view is unchanged; try again when the connection returns.',
  resumeFallback: 'Welcome back. The accepted working view is open so the conversation can continue.',
  resumeFailure: 'We could not resume the conversation. Your working view is still available; try again when the connection returns.',
});

const EMPTY_SCREEN_CONTEXT: ScreenChatContext = Object.freeze({
  screenId: 'discovery',
  entities: [],
  selection: null,
  captured: [],
  outstanding: [],
});
const NOOP_ASYNC = async (): Promise<void> => {};
const NOOP = (): void => {};
const SESSION_OPEN_SIGNAL = '[session opened]';
const SESSION_RESUME_SIGNAL = '[session resumed]';

/** What rides one facilitator reply: the pages it drew on (093) and the facts
 * it captured (100, decision 7). Both belong to that reply, not to the panel. */
function replyExtras(model: DoneModel | undefined): Partial<ConversationMessage> {
  const facts = turnFactsFrom(model?.factEvents);
  return {
    ...(model?.citations?.length ? { citations: model.citations } : {}),
    ...(facts.length ? { facts } : {}),
  };
}

/** The files the consultant attached to a turn, as the panel shows them back.
 * The browser knows the name, type and size; where the bytes were filed is the
 * server's answer and arrives with the persisted turn. */
function attachedFiles(files: File[]): AttachmentRecord[] {
  return files.map((file) => ({
    filename: file.name,
    mime: file.type || '',
    size: file.size,
  }));
}

/** The attachments a persisted turn recorded, kept only where they name a file
 * so a reloaded conversation shows exactly what a live one does. */
function seededAttachments(value: unknown): AttachmentRecord[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const record = entry as Record<string, unknown>;
    const filename = typeof record.filename === 'string' ? record.filename : '';
    if (!filename) return [];
    return [{
      filename,
      mime: typeof record.mime === 'string' ? record.mime : '',
      size: typeof record.size === 'number' ? record.size : 0,
      ...(typeof record.kind === 'string' ? { kind: record.kind } : {}),
      ...(typeof record.blobPath === 'string' ? { blobPath: record.blobPath } : {}),
      ...(typeof record.sourceRef === 'string' ? { sourceRef: record.sourceRef } : {}),
    }];
  });
}

function resumedMarker(history: ConversationMessage[]): ConversationMessage {
  const timestamp = [...history]
    .reverse()
    .find((turn) => turn.type !== 'transcript' && turn.timestamp)?.timestamp;
  let lastActive = 'previously';
  if (timestamp) {
    const parsed = new Date(timestamp);
    if (!Number.isNaN(parsed.getTime())) {
      lastActive = parsed.toLocaleDateString('en-GB', { weekday: 'long' });
    }
  }
  return { type: 'transcript', text: `Resumed · last active ${lastActive}`, timestamp: '' };
}

export function useChat(configuration: UseChatConfiguration): UseChatReturn {
  const facilitation = configuration.kind === 'facilitation';
  const discoveryContextId = configuration.kind === 'discovery'
    ? configuration.contextId
    : undefined;
  const sessionId = configuration.sessionId;
  const copy = facilitation ? (configuration.copy ?? DEFAULT_COPY) : DEFAULT_COPY;
  const engagement = useEngagement();
  const refreshEngagement = engagement?.refreshEngagement ?? NOOP_ASYNC;

  const [conversationHistory, setConversationHistory] = useState<ConversationMessage[]>([]);
  const [historySeeded, setHistorySeeded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [turnSettling, setTurnSettling] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<CandidateItem[]>([]);
  const [activities, setActivities] = useState<ChatActivity[]>([]);

  const sessionIdRef = useRef<string | null>(sessionId);
  sessionIdRef.current = sessionId;
  const modeRef = useRef<ConversationMode>(
    configuration.kind === 'discovery' ? configuration.mode : 'discovery',
  );
  modeRef.current = configuration.kind === 'discovery' ? configuration.mode : 'discovery';
  const memorySummaryRef = useRef(
    configuration.kind === 'discovery' ? configuration.memorySummary : '',
  );
  memorySummaryRef.current =
    configuration.kind === 'discovery' ? configuration.memorySummary : '';
  const screenContextRef = useRef<ScreenChatContext>(
    facilitation ? configuration.screenContext : EMPTY_SCREEN_CONTEXT,
  );
  screenContextRef.current = facilitation
    ? configuration.screenContext
    : EMPTY_SCREEN_CONTEXT;
  const factEventsRef = useRef(
    facilitation ? configuration.onTurnFactEvents : undefined,
  );
  factEventsRef.current = facilitation ? configuration.onTurnFactEvents : undefined;
  const surfaceActionsRef = useRef(
    facilitation ? configuration.onSurfaceActions : undefined,
  );
  surfaceActionsRef.current = facilitation ? configuration.onSurfaceActions : undefined;
  const streamAbortRef = useRef<AbortController | null>(null);
  const textQueueRef = useRef<Promise<void>>(Promise.resolve());
  const engagementRefreshRef = useRef<Promise<void>>(Promise.resolve());

  const handleActivity = useCallback((event: ChatActivityEvent) => {
    setActivities((previous) => applyActivityEvent(previous, event));
  }, []);

  const noopNewModel = useCallback<Dispatch<SetStateAction<NewModel>>>(() => {}, []);
  const noopFeatures = useCallback((_payload: DiscoveryFeaturesPayload) => {}, []);
  const setNewModel = configuration.kind === 'discovery'
    ? configuration.setNewModel
    : noopNewModel;
  const setDiscoveryFeatures = configuration.kind === 'discovery'
    ? configuration.setDiscoveryFeatures
    : noopFeatures;

  const imports = useImports({
    sessionIdRef,
    setNewModel,
    setDiscoveryFeatures,
  });

  const applyFacilitationEnvelope = useCallback(
    (model: DoneModel | undefined) => {
      if (!facilitation) return Promise.resolve();
      setCandidates(Array.isArray(model?.candidates) ? model.candidates as CandidateItem[] : []);
      if (Array.isArray(model?.factEvents) && model.factEvents.length > 0) {
        factEventsRef.current?.(model.factEvents);
      }
      if (Array.isArray(model?.surfaceActions) && model.surfaceActions.length > 0) {
        surfaceActionsRef.current?.(model.surfaceActions);
      }
      const refresh = Promise.resolve(refreshEngagement()).catch(() => {});
      engagementRefreshRef.current = refresh;
      return refresh;
    },
    [facilitation, refreshEngagement],
  );

  const applyTurnResult = configuration.kind === 'discovery'
    ? configuration.applyTurnResult
    : NOOP;
  const newModel = configuration.kind === 'discovery'
    ? configuration.newModel
    : ({ flowText: '', nextQuestion: '' } as NewModel);
  const voice = useVoice({
    sessionIdRef,
    modeRef,
    memorySummaryRef,
    conversationHistory,
    setConversationHistory,
    setNewModel,
    applyTurnResult,
    newModelFlowText: newModel.flowText,
    newModelNextQuestion: newModel.nextQuestion,
    context: facilitation ? 'facilitation' : discoveryContextId,
    screenContextRef: facilitation ? screenContextRef : undefined,
    speakReplies: configuration.kind === 'facilitation'
      ? (configuration.speakReplies ?? true)
      : undefined,
    openingLine: facilitation ? null : undefined,
    voiceModeForSession: facilitation ? 'watch' : undefined,
    onDoneEnvelope: facilitation ? applyFacilitationEnvelope : undefined,
  }) ?? ({} as ReturnType<typeof useVoice>);

  const callStatus = voice.callStatus ?? 'Disconnected';
  const isVoiceWaiting = voice.isVoiceWaiting ?? false;
  const startVoiceCall = voice.startVoiceCall ?? NOOP_ASYNC;
  const endVoiceCall = voice.endVoiceCall ?? NOOP;

  const handleTextMessage = useCallback(async (message: string, files: File[] = []) => {
    if (!facilitation && !message.trim() && files.length === 0) return;
    const id = sessionIdRef.current;
    if (!id) return;

    // 100 (owner decision 8): an attachment alone is a turn. The files ride
    // the turn as attachments rather than as text glued onto the message, so a
    // turn with nothing typed still shows what was sent - and shows the same
    // thing after a reload, where the server's own record is all there is.
    setConversationHistory((previous) => [...previous, {
      id: `user-${Date.now()}`,
      type: 'user',
      text: message || '',
      timestamp: new Date().toLocaleTimeString(),
      ...(files.length ? { attachments: attachedFiles(files) } : {}),
    }]);
    setIsLoading(true);
    setChatError(null);
    setActivities([]);
    streamAbortRef.current?.abort();
    const controller = new AbortController();
    streamAbortRef.current = controller;

    const dispatch = async () => {
      let renderedAgent = false;
      engagementRefreshRef.current = Promise.resolve();
      try {
        await streamChat2(
          id,
          message,
          {
            onActivity: handleActivity,
            onNextQuestion: facilitation ? undefined : (text) => {
              if (sessionIdRef.current !== id || renderedAgent) return;
              setConversationHistory((previous) => [...previous, {
                type: 'agent',
                text: stripDsnFromText(text),
                timestamp: new Date().toLocaleTimeString(),
              }]);
              renderedAgent = true;
              setIsLoading(false);
            },
            onDone: (agentResponse, model) => {
              if (sessionIdRef.current !== id) return;
              if (facilitation) applyFacilitationEnvelope(model);
              else applyTurnResult(model);
              if (!renderedAgent && agentResponse) {
                setConversationHistory((previous) => [...previous, {
                  type: 'agent',
                  text: facilitation ? agentResponse : stripDsnFromText(agentResponse),
                  timestamp: new Date().toLocaleTimeString(),
                  // 093/100: the pages this reply drew on and the facts it
                  // captured ride with the reply, so the footer sits under the
                  // words that produced them.
                  ...(facilitation ? replyExtras(model) : {}),
                }]);
                renderedAgent = true;
              }
            },
          },
          files,
          controller.signal,
          facilitation ? 'facilitation' : discoveryContextId,
          facilitation ? screenContextRef.current : undefined,
          undefined,
          facilitation
            ? {
              inputId: createFacilitationId(files.length ? 'attachment' : 'text'),
              modality: files.length ? 'attachment' : 'text',
            }
            : undefined,
        );
      } catch (error) {
        if (facilitation) setChatError(copy.sendFailure);
        else console.error('Error sending message:', error);
      } finally {
        if (facilitation) await engagementRefreshRef.current;
        setIsLoading(false);
        setTurnSettling(false);
      }
    };

    if (facilitation) {
      await dispatch();
      return;
    }
    setTurnSettling(true);
    textQueueRef.current = textQueueRef.current.catch(() => {}).then(dispatch);
    await textQueueRef.current;
  }, [
    applyFacilitationEnvelope,
    applyTurnResult,
    copy.sendFailure,
    discoveryContextId,
    facilitation,
    handleActivity,
  ]);

  const runHiddenTurn = useCallback(async (
    signal: typeof SESSION_OPEN_SIGNAL | typeof SESSION_RESUME_SIGNAL,
  ) => {
    if (!facilitation) return;
    const id = sessionIdRef.current;
    if (!id) return;
    if (signal === SESSION_RESUME_SIGNAL) {
      setConversationHistory((previous) => {
        if (previous.at(-1)?.type === 'transcript'
          && previous.at(-1)?.text.startsWith('Resumed')) return previous;
        return [...previous, resumedMarker(previous)];
      });
    }
    setIsLoading(true);
    setChatError(null);
    setActivities([]);
    streamAbortRef.current?.abort();
    const controller = new AbortController();
    streamAbortRef.current = controller;
    let replied = false;
    engagementRefreshRef.current = Promise.resolve();
    try {
      await streamChat2(
        id,
        signal,
        {
          onActivity: handleActivity,
          onDone: (agentResponse, model) => {
            if (agentResponse) {
              setConversationHistory((previous) => [...previous, {
                type: 'agent',
                text: agentResponse,
                timestamp: new Date().toLocaleTimeString(),
                ...replyExtras(model),
              }]);
              replied = true;
            }
            applyFacilitationEnvelope(model);
          },
        },
        [],
        controller.signal,
        'facilitation',
        screenContextRef.current,
        undefined,
        {
          inputId: createFacilitationId(
            signal === SESSION_OPEN_SIGNAL ? 'system' : 'resume',
          ),
          modality: 'system',
        },
      );
      if (!replied) {
        const fallback = signal === SESSION_OPEN_SIGNAL
          ? copy.openingFallback(new Date().getHours())
          : copy.resumeFallback;
        setConversationHistory((previous) => [...previous, {
          type: 'agent',
          text: fallback,
          timestamp: new Date().toLocaleTimeString(),
        }]);
      }
    } catch {
      if (!replied && signal === SESSION_OPEN_SIGNAL) {
        setConversationHistory((previous) => [...previous, {
          type: 'agent',
          text: copy.openingFallback(new Date().getHours()),
          timestamp: new Date().toLocaleTimeString(),
        }]);
      } else if (!replied) {
        setChatError(copy.resumeFailure);
      }
    } finally {
      await engagementRefreshRef.current;
      setIsLoading(false);
    }
  }, [applyFacilitationEnvelope, copy, facilitation, handleActivity]);

  const openSession = useCallback(
    () => runHiddenTurn(SESSION_OPEN_SIGNAL),
    [runHiddenTurn],
  );
  const resumeSession = useCallback(
    () => runHiddenTurn(SESSION_RESUME_SIGNAL),
    [runHiddenTurn],
  );

  useEffect(() => {
    if (!facilitation || !sessionId || typeof fetch !== 'function') return undefined;
    let cancelled = false;
    setHistorySeeded(false);
    (async () => {
      try {
        const response = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}`);
        if (!response.ok || cancelled) return;
        const session = await response.json().catch(() => null) as
          | { conversationHistory?: unknown[] }
          | null;
        if (!session || !Array.isArray(session.conversationHistory) || cancelled) return;
        const resumed = session.conversationHistory.some((turn) =>
          Boolean(turn) && typeof turn === 'object'
          && (turn as { type?: unknown }).type === 'user'
          && (turn as { text?: unknown }).text === SESSION_RESUME_SIGNAL);
        const seeded = session.conversationHistory
          .filter((turn): turn is Record<string, unknown> =>
            Boolean(turn) && typeof turn === 'object'
            && !((turn as { type?: unknown }).type === 'user'
              && [(SESSION_OPEN_SIGNAL), (SESSION_RESUME_SIGNAL)]
                .includes((turn as { text?: string }).text as typeof SESSION_OPEN_SIGNAL)))
          .map((turn, index) => {
            // 100 (owner decision 8): a turn that was nothing but a file keeps
            // showing that file when the conversation is reopened.
            const attachments = seededAttachments(turn.attachments);
            return {
              id: typeof turn.id === 'string' ? turn.id : `seed-${index}`,
              type: turn.type === 'user' ? 'user' as const : 'agent' as const,
              text: typeof turn.text === 'string' ? turn.text : '',
              timestamp: typeof turn.timestamp === 'string' ? turn.timestamp : '',
              ...(attachments.length ? { attachments } : {}),
            };
          });
        if (!cancelled) {
          setConversationHistory(resumed && seeded.length
            ? [...seeded, resumedMarker(seeded)]
            : seeded);
          setHistorySeeded(true);
        }
      } catch {
        // A failed seed remains visibly unseeded; the shell will not mis-greet.
      }
    })();
    return () => { cancelled = true; };
  }, [facilitation, sessionId]);

  useEffect(() => () => streamAbortRef.current?.abort(), []);

  const acceptCandidates = useCallback(async (accepted: AcceptedCandidate[]) => {
    if (!facilitation || !accepted.length) return;
    const id = sessionIdRef.current;
    if (!id) return;
    setActivities([]);
    engagementRefreshRef.current = Promise.resolve();
    try {
      await streamChat2(
        id,
        '',
        {
          onActivity: handleActivity,
          onDone: (agentResponse, model) => {
            if (agentResponse) {
              setConversationHistory((previous) => [...previous, {
                type: 'agent',
                text: agentResponse,
                timestamp: new Date().toLocaleTimeString(),
                ...replyExtras(model),
              }]);
            }
            applyFacilitationEnvelope(model);
          },
        },
        [],
        new AbortController().signal,
        'facilitation',
        screenContextRef.current,
        accepted,
        { inputId: createFacilitationId('review'), modality: 'system' },
      );
      await engagementRefreshRef.current;
    } catch {
      // Candidate acceptance stays retryable and never tears down the surface.
    }
  }, [applyFacilitationEnvelope, facilitation, handleActivity]);

  const replaceConversationHistory = useCallback(
    (history: ConversationMessage[]) => {
      setConversationHistory(history);
      // A host-owned session loader has now completed the same transcript
      // seed that facilitation-mode chat performs itself. First-message
      // relays can safely deliver only after this point.
      setHistorySeeded(true);
    },
    [],
  );
  const resetConversationHistory = useCallback(() => {
    setConversationHistory([]);
    setActivities([]);
    setCandidates([]);
    setChatError(null);
  }, []);

  return useMemo<UseChatReturn>(() => ({
    conversationHistory,
    isLoading: isLoading || isVoiceWaiting,
    inputBusy: isLoading || turnSettling || isVoiceWaiting,
    callStatus,
    handleTextMessage,
    startVoiceCall,
    endVoiceCall,
    importFromDocument: imports.importFromDocument,
    ...(configuration.kind === 'discovery' ? {
      onEscape: configuration.transitionOffered
        ? configuration.dismissOffer
        : undefined,
    } : {}),
    sessionError:
      (configuration.kind === 'discovery' ? configuration.sessionError : null)
      ?? chatError,
    activities,
    openSession,
    resumeSession,
    candidates,
    acceptCandidates,
    historySeeded,
    isImportingDocument: imports.isImportingDocument,
    importFromMermaid: imports.importFromMermaid,
    isImportingMermaid: imports.isImportingMermaid,
    setFlowText: imports.setFlowText,
    isSavingFlowText: imports.isSavingFlowText,
    replaceConversationHistory,
    resetConversationHistory,
  }), [
    acceptCandidates,
    activities,
    callStatus,
    candidates,
    chatError,
    configuration,
    conversationHistory,
    endVoiceCall,
    handleTextMessage,
    historySeeded,
    imports,
    isLoading,
    isVoiceWaiting,
    openSession,
    replaceConversationHistory,
    resetConversationHistory,
    resumeSession,
    startVoiceCall,
    turnSettling,
  ]);
}
